import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Inbox,
  FileText,
  Image as ImageIcon,
  Film,
  LogOut,
  Trash2,
  Plus,
  Lock,
  Mail,
  Phone,
  MessageSquare,
  Users,
  CalendarDays,
  BookOpen,
  Pencil,
} from "lucide-react";
import Logo, { KiteMark } from "@/components/Logo";
import FolkBorder from "@/components/FolkBorder";
import api, { fmtErr } from "@/lib/api";

const TABS = [
  { id: "leads", label: "Leads", icon: Inbox },
  { id: "content", label: "Content", icon: FileText },
  { id: "gallery", label: "Gallery", icon: ImageIcon },
  { id: "reels", label: "Reels", icon: Film },
  { id: "team", label: "Team", icon: Users },
  { id: "events", label: "Events", icon: CalendarDays },
  { id: "blog", label: "Blog", icon: BookOpen },
];

const TEXT_FIELDS = [
  ["hero_title", "Hero Title"],
  ["hero_kicker", "Hero Kicker (e.g. TEAM ODISHA)"],
  ["tagline", "Tagline"],
  ["about_title", "About Title"],
  ["about_punch", "About Punch Line"],
  ["vision_title", "Vision Title"],
  ["mission_title", "Mission Title"],
  ["contact_email", "Contact Email"],
  ["contact_phone", "Contact Phone"],
  ["contact_location", "Location"],
  ["instagram_url", "Instagram URL"],
  ["youtube_url", "YouTube URL"],
  ["facebook_url", "Facebook URL"],
  ["team_title", "Team Section Title"],
  ["collection_title", "Collection Section Title"],
  ["team_image", "Team Image URL"],
  ["hero_image", "Hero Image URL"],
  ["about_image", "About Image URL"],
  ["contact_image", "Contact Image URL"],
  ["gallery_script", "Gallery Script Line"],
  ["gallery_heading", "Gallery Heading"],
  ["reels_script", "Reels Script Line"],
  ["reels_heading", "Reels Heading"],
  ["contact_script", "Contact Script Line"],
  ["blog_script", "Blog Script Line"],
  ["blog_heading", "Blog Heading"],
  ["events_script", "Events Script Line"],
  ["events_heading", "Events Page Heading"],
  ["events_cta_script", "Events CTA Line"],
  ["footer_script", "Footer Script Line 1"],
  ["footer_script_2", "Footer Script Line 2"],
  ["footer_links_title", "Footer Links Title"],
  ["whatsapp_url", "WhatsApp Link (wa.me/91...)"],
];

const AREA_FIELDS = [
  ["hero_text", "Hero Sub Text"],
  ["about_text", "About Text"],
  ["vision_text", "Vision Text"],
  ["mission_text", "Mission Text"],
  ["team_text", "Team Text"],
  ["collection_text", "Collection Text"],
  ["reels_sub", "Reels Sub Text"],
  ["blog_sub", "Blog Sub Text"],
  ["events_sub", "Events Sub Text"],
  ["events_cta_text", "Events CTA Text"],
  ["footer_blurb", "Footer Blurb"],
];

const LIST_FIELDS = [
  ["marquee_items", "Marquee Items (comma separated)"],
  ["values", "Values (comma separated)"],
];

const inputCls =
  "w-full bg-paper border-2 border-navy/20 rounded-lg px-4 py-2.5 font-body text-navy text-sm focus:outline-none focus:border-verm transition-colors duration-300";

const Login = ({ onDone }) => {
  const [email, setEmail] = useState("admin@windskaters.com");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      onDone(data);
    } catch (err) {
      toast.error(fmtErr(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-paper flex flex-col" data-testid="admin-login-page">
      <FolkBorder />
      <div className="flex-1 grid place-items-center px-5 py-16">
        <form
          onSubmit={submit}
          className="w-full max-w-md bg-paperdeep/60 border-2 border-navy/15 rounded-2xl p-8 sm:p-10 shadow-xl shadow-navy/10"
          data-testid="admin-login-form"
        >
          <KiteMark className="w-14 h-14 mx-auto" />
          <h1 className="mt-4 text-center font-display font-bold text-navy text-3xl" data-testid="admin-login-heading">
            Wind Skaters Admin
          </h1>
          <p className="text-center font-body text-navy/60 text-sm mt-1">Sign in to manage the website</p>
          <div className="mt-8 space-y-4">
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Email"
              required
              data-testid="admin-email-input"
              className={inputCls}
            />
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Password"
              required
              data-testid="admin-password-input"
              className={inputCls}
            />
            <button
              type="submit"
              disabled={busy}
              data-testid="admin-login-button"
              className="w-full inline-flex items-center justify-center gap-2 bg-navy text-paper font-semibold px-6 py-3 rounded-full border-2 border-marigold hover:bg-verm hover:border-verm transition-colors duration-300 disabled:opacity-60"
            >
              <Lock className="w-4 h-4" />
              {busy ? "Signing in..." : "Sign In"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const LeadsTab = () => {
  const [leads, setLeads] = useState(null);

  const load = () => api.get("/admin/leads").then((r) => setLeads(r.data)).catch((e) => toast.error(fmtErr(e)));
  useEffect(() => {
    load();
  }, []);

  const remove = async (id) => {
    try {
      await api.delete(`/admin/leads/${id}`);
      toast.success("Lead deleted");
      setLeads((l) => l.filter((x) => x.id !== id));
    } catch (e) {
      toast.error(fmtErr(e));
    }
  };

  if (!leads) return <p className="font-body text-navy/60">Loading leads…</p>;

  return (
    <div data-testid="admin-leads-tab">
      {leads.length === 0 ? (
        <div className="border-2 border-dashed border-navy/25 rounded-2xl p-12 text-center font-body text-navy/60" data-testid="leads-empty">
          No messages yet. Leads from the contact form will land here.
        </div>
      ) : (
        <div className="grid gap-4" data-testid="leads-list">
          {leads.map((lead) => (
            <div
              key={lead.id}
              className="bg-paper border-2 border-navy/15 rounded-xl p-5 shadow-sm"
              data-testid="lead-card"
            >
              <div className="flex items-start justify-between gap-4">
                <div>
                  <p className="font-display font-bold text-navy text-xl" data-testid="lead-name">
                    {lead.name}
                  </p>
                  <p className="font-body text-navy/70 text-sm mt-1 flex items-center gap-2" data-testid="lead-email">
                    <Mail className="w-3.5 h-3.5" /> {lead.email}
                  </p>
                  {lead.phone ? (
                    <p className="font-body text-navy/70 text-sm mt-0.5 flex items-center gap-2" data-testid="lead-phone">
                      <Phone className="w-3.5 h-3.5" /> {lead.phone}
                    </p>
                  ) : null}
                  <p className="font-body text-navy/60 text-xs mt-0.5" data-testid="lead-date">
                    {new Date(lead.created_at).toLocaleString()}
                  </p>
                </div>
                <button
                  onClick={() => remove(lead.id)}
                  data-testid="lead-delete-button"
                  aria-label="Delete lead"
                  className="text-navy/40 hover:text-verm transition-colors duration-300 p-2"
                >
                  <Trash2 className="w-5 h-5" />
                </button>
              </div>
              <p className="mt-3 font-body text-navy/85 text-sm leading-relaxed border-t border-navy/10 pt-3 flex gap-2" data-testid="lead-message">
                <MessageSquare className="w-4 h-4 shrink-0 text-verm" />
                {lead.message}
              </p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const ContentTab = ({ content, onSaved }) => {
  const [form, setForm] = useState(content);
  const [busy, setBusy] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.put("/admin/content", form);
      onSaved(data);
      toast.success("Website updated — refresh the home page to see it live");
    } catch (err) {
      toast.error(fmtErr(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={save} className="space-y-5" data-testid="admin-content-tab">
      <div className="grid sm:grid-cols-2 gap-4">
        {TEXT_FIELDS.map(([key, label]) => (
          <div key={key}>
            <label className="block font-body font-semibold text-navy text-xs mb-1">{label}</label>
            <input value={form[key] || ""} onChange={set(key)} data-testid={`content-input-${key}`} className={inputCls} />
          </div>
        ))}
      </div>
      {AREA_FIELDS.map(([key, label]) => (
        <div key={key}>
          <label className="block font-body font-semibold text-navy text-xs mb-1">{label}</label>
          <textarea
            rows={3}
            value={form[key] || ""}
            onChange={set(key)}
            data-testid={`content-input-${key}`}
            className={`${inputCls} resize-none`}
          />
        </div>
      ))}
      {LIST_FIELDS.map(([key, label]) => (
        <div key={key}>
          <label className="block font-body font-semibold text-navy text-xs mb-1">{label}</label>
          <input
            value={(form[key] || []).join(", ")}
            onChange={(e) => setForm((f) => ({ ...f, [key]: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) }))}
            data-testid={`content-input-${key}`}
            className={inputCls}
          />
        </div>
      ))}
      <button
        type="submit"
        disabled={busy}
        data-testid="content-save-button"
        className="inline-flex items-center gap-2 bg-navy text-paper font-semibold px-8 py-3 rounded-full border-2 border-marigold hover:bg-verm hover:border-verm transition-colors duration-300 disabled:opacity-60"
      >
        {busy ? "Saving..." : "Save Changes"}
      </button>
    </form>
  );
};

const GalleryTab = () => {
  const [images, setImages] = useState(null);
  const [url, setUrl] = useState("");
  const [caption, setCaption] = useState("");

  useEffect(() => {
    api.get("/gallery").then((r) => setImages(r.data)).catch((e) => toast.error(fmtErr(e)));
  }, []);

  const add = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/admin/gallery", { url, caption });
      setImages((l) => [...(l || []), data]);
      setUrl("");
      setCaption("");
      toast.success("Photo added to the gallery");
    } catch (err) {
      toast.error(fmtErr(err));
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/admin/gallery/${id}`);
      setImages((l) => l.filter((x) => x.id !== id));
      toast.success("Photo removed");
    } catch (err) {
      toast.error(fmtErr(err));
    }
  };

  if (!images) return <p className="font-body text-navy/60">Loading gallery…</p>;

  return (
    <div data-testid="admin-gallery-tab">
      <form onSubmit={add} className="flex flex-col sm:flex-row gap-3 mb-6" data-testid="gallery-add-form">
        <input
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          placeholder="Image URL (https://...)"
          required
          data-testid="gallery-url-input"
          className={inputCls}
        />
        <input
          value={caption}
          onChange={(e) => setCaption(e.target.value)}
          placeholder="Caption"
          data-testid="gallery-caption-input"
          className={`${inputCls} sm:max-w-xs`}
        />
        <button
          type="submit"
          data-testid="gallery-add-button"
          className="inline-flex items-center justify-center gap-2 bg-navy text-paper font-semibold px-6 py-2.5 rounded-full border-2 border-marigold hover:bg-verm hover:border-verm transition-colors duration-300 shrink-0"
        >
          <Plus className="w-4 h-4" /> Add
        </button>
      </form>
      {images.length === 0 ? (
        <div className="border-2 border-dashed border-navy/25 rounded-2xl p-10 text-center font-body text-navy/60">
          No photos yet.
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4" data-testid="gallery-admin-grid">
          {images.map((img) => (
            <div key={img.id} className="relative group rounded-xl overflow-hidden border-2 border-navy/15" data-testid="gallery-admin-item">
              <img src={img.url} alt={img.caption || "gallery"} className="w-full h-36 object-cover" />
              {img.caption ? (
                <p className="absolute inset-x-0 bottom-0 bg-navy/80 text-paper text-xs px-3 py-1.5 truncate">{img.caption}</p>
              ) : null}
              <button
                onClick={() => remove(img.id)}
                data-testid="gallery-delete-button"
                aria-label="Delete image"
                className="absolute top-2 right-2 w-8 h-8 rounded-full bg-paper/90 text-verm grid place-items-center opacity-0 group-hover:opacity-100 transition-opacity duration-300"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const ReelsTab = () => {
  const [reels, setReels] = useState(null);
  const [url, setUrl] = useState("");
  const [title, setTitle] = useState("");

  useEffect(() => {
    api.get("/reels").then((r) => setReels(r.data)).catch((e) => toast.error(fmtErr(e)));
  }, []);

  const add = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/admin/reels", { url, title });
      setReels((l) => [data, ...(l || [])]);
      setUrl("");
      setTitle("");
      toast.success("Reel added — it is live on the site now");
    } catch (err) {
      toast.error(fmtErr(err));
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/admin/reels/${id}`);
      setReels((l) => l.filter((x) => x.id !== id));
      toast.success("Reel removed");
    } catch (err) {
      toast.error(fmtErr(err));
    }
  };

  if (!reels) return <p className="font-body text-navy/60">Loading reels…</p>;

  return (
    <div data-testid="admin-reels-tab">
      <form onSubmit={add} className="flex flex-col sm:flex-row gap-3 mb-6" data-testid="reels-add-form">
        <input
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          placeholder="Instagram reel or YouTube link (https://...)"
          required
          data-testid="reels-url-input"
          className={inputCls}
        />
        <input
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Title (optional)"
          data-testid="reels-title-input"
          className={`${inputCls} sm:max-w-xs`}
        />
        <button
          type="submit"
          data-testid="reels-add-button"
          className="inline-flex items-center justify-center gap-2 bg-navy text-paper font-semibold px-6 py-2.5 rounded-full border-2 border-marigold hover:bg-verm hover:border-verm transition-colors duration-300 shrink-0"
        >
          <Plus className="w-4 h-4" /> Add
        </button>
      </form>
      {reels.length === 0 ? (
        <div className="border-2 border-dashed border-navy/25 rounded-2xl p-10 text-center font-body text-navy/60" data-testid="reels-admin-empty">
          No reels yet. Paste an Instagram reel or YouTube short link above.
        </div>
      ) : (
        <div className="space-y-3" data-testid="reels-admin-list">
          {reels.map((reel) => (
            <div
              key={reel.id}
              className="flex items-center justify-between gap-4 bg-paper border-2 border-navy/15 rounded-xl p-4"
              data-testid="reels-admin-item"
            >
              <div className="min-w-0">
                <span
                  className={`inline-block text-[10px] font-bold tracking-widest uppercase px-2 py-0.5 rounded-full ${
                    reel.platform === "instagram" ? "bg-verm/10 text-verm" : "bg-navy/10 text-navy"
                  }`}
                  data-testid="reels-admin-platform"
                >
                  {reel.platform}
                </span>
                <p className="font-body text-navy text-sm mt-1 truncate" data-testid="reels-admin-title">
                  {reel.title || reel.url}
                </p>
              </div>
              <button
                onClick={() => remove(reel.id)}
                data-testid="reels-delete-button"
                aria-label="Delete reel"
                className="text-navy/40 hover:text-verm transition-colors duration-300 p-2"
              >
                <Trash2 className="w-5 h-5" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const TeamTab = () => {
  const [members, setMembers] = useState(null);
  const [form, setForm] = useState({ name: "", role: "", story: "", photo_url: "" });

  useEffect(() => {
    api.get("/team").then((r) => setMembers(r.data)).catch((e) => toast.error(fmtErr(e)));
  }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const add = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/admin/team", form);
      setMembers((l) => [...(l || []), data]);
      setForm({ name: "", role: "", story: "", photo_url: "" });
      toast.success("Team member added — their story is live on the site");
    } catch (err) {
      toast.error(fmtErr(err));
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/admin/team/${id}`);
      setMembers((l) => l.filter((x) => x.id !== id));
      toast.success("Team member removed");
    } catch (err) {
      toast.error(fmtErr(err));
    }
  };

  if (!members) return <p className="font-body text-navy/60">Loading team…</p>;

  return (
    <div data-testid="admin-team-tab">
      <form onSubmit={add} className="grid sm:grid-cols-2 gap-3 mb-6" data-testid="team-add-form">
        <input
          value={form.name}
          onChange={set("name")}
          placeholder="Name *"
          required
          data-testid="team-name-input"
          className={inputCls}
        />
        <input
          value={form.role}
          onChange={set("role")}
          placeholder="Role (e.g. Kite Pilot)"
          data-testid="team-role-input"
          className={inputCls}
        />
        <textarea
          value={form.story}
          onChange={set("story")}
          placeholder="Their story *"
          required
          rows={2}
          data-testid="team-story-input"
          className={`${inputCls} resize-none sm:col-span-2`}
        />
        <input
          value={form.photo_url}
          onChange={set("photo_url")}
          placeholder="Photo URL (optional, https://...)"
          data-testid="team-photo-input"
          className={`${inputCls} sm:col-span-2`}
        />
        <button
          type="submit"
          data-testid="team-add-button"
          className="inline-flex items-center justify-center gap-2 bg-navy text-paper font-semibold px-6 py-2.5 rounded-full border-2 border-marigold hover:bg-verm hover:border-verm transition-colors duration-300 sm:col-span-2"
        >
          <Plus className="w-4 h-4" /> Add Team Member
        </button>
      </form>
      {members.length === 0 ? (
        <div className="border-2 border-dashed border-navy/25 rounded-2xl p-10 text-center font-body text-navy/60" data-testid="team-admin-empty">
          No team members yet. Add your flyers above and their stories appear on the site.
        </div>
      ) : (
        <div className="space-y-3" data-testid="team-admin-list">
          {members.map((m) => (
            <div
              key={m.id}
              className="flex items-center justify-between gap-4 bg-paper border-2 border-navy/15 rounded-xl p-4"
              data-testid="team-admin-item"
            >
              <div className="min-w-0">
                <p className="font-display font-bold text-navy" data-testid="team-admin-name">
                  {m.name}
                  {m.role ? <span className="font-body text-verm text-xs font-bold uppercase tracking-wider ml-2">{m.role}</span> : null}
                </p>
                {m.story ? (
                  <p className="font-body text-navy/70 text-sm mt-0.5 truncate" data-testid="team-admin-story">
                    {m.story}
                  </p>
                ) : null}
              </div>
              <button
                onClick={() => remove(m.id)}
                data-testid="team-delete-button"
                aria-label="Delete team member"
                className="text-navy/40 hover:text-verm transition-colors duration-300 p-2"
              >
                <Trash2 className="w-5 h-5" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const EventsTab = () => {
  const [events, setEvents] = useState(null);
  const [form, setForm] = useState({ title: "", event_date: "", location: "", description: "" });

  useEffect(() => {
    api.get("/events").then((r) => setEvents(r.data)).catch((e) => toast.error(fmtErr(e)));
  }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const add = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/admin/events", form);
      setEvents((l) => [...(l || []), data].sort((a, b) => String(a.event_date).localeCompare(String(b.event_date))));
      setForm({ title: "", event_date: "", location: "", description: "" });
      toast.success("Event added — it is live on the Events page");
    } catch (err) {
      toast.error(fmtErr(err));
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/admin/events/${id}`);
      setEvents((l) => l.filter((x) => x.id !== id));
      toast.success("Event removed");
    } catch (err) {
      toast.error(fmtErr(err));
    }
  };

  if (!events) return <p className="font-body text-navy/60">Loading events…</p>;

  return (
    <div data-testid="admin-events-tab">
      <form onSubmit={add} className="grid sm:grid-cols-2 gap-3 mb-6" data-testid="events-add-form">
        <input
          value={form.title}
          onChange={set("title")}
          placeholder="Event title *"
          required
          data-testid="events-title-input"
          className={inputCls}
        />
        <input
          type="date"
          value={form.event_date}
          onChange={set("event_date")}
          data-testid="events-date-input"
          className={inputCls}
        />
        <input
          value={form.location}
          onChange={set("location")}
          placeholder="Location (e.g. Puri Beach, Odisha)"
          data-testid="events-location-input"
          className={`${inputCls} sm:col-span-2`}
        />
        <textarea
          value={form.description}
          onChange={set("description")}
          placeholder="Details — time, what to bring, who it's for..."
          rows={2}
          data-testid="events-description-input"
          className={`${inputCls} resize-none sm:col-span-2`}
        />
        <button
          type="submit"
          data-testid="events-add-button"
          className="inline-flex items-center justify-center gap-2 bg-navy text-paper font-semibold px-6 py-2.5 rounded-full border-2 border-marigold hover:bg-verm hover:border-verm transition-colors duration-300 sm:col-span-2"
        >
          <Plus className="w-4 h-4" /> Add Event
        </button>
      </form>
      {events.length === 0 ? (
        <div className="border-2 border-dashed border-navy/25 rounded-2xl p-10 text-center font-body text-navy/60" data-testid="events-admin-empty">
          No events yet. Add your festival and meetup dates above.
        </div>
      ) : (
        <div className="space-y-3" data-testid="events-admin-list">
          {events.map((ev) => (
            <div
              key={ev.id}
              className="flex items-center justify-between gap-4 bg-paper border-2 border-navy/15 rounded-xl p-4"
              data-testid="events-admin-item"
            >
              <div className="min-w-0">
                <p className="font-display font-bold text-navy" data-testid="events-admin-title">
                  {ev.title}
                </p>
                <p className="font-body text-navy/70 text-sm mt-0.5" data-testid="events-admin-meta">
                  {[ev.event_date, ev.location].filter(Boolean).join(" · ") || "No date set"}
                </p>
              </div>
              <button
                onClick={() => remove(ev.id)}
                data-testid="events-delete-button"
                aria-label="Delete event"
                className="text-navy/40 hover:text-verm transition-colors duration-300 p-2"
              >
                <Trash2 className="w-5 h-5" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const EMPTY_POST = { title: "", body: "", image_url: "", author: "" };

const BlogTab = () => {
  const [posts, setPosts] = useState(null);
  const [form, setForm] = useState(EMPTY_POST);
  const [editId, setEditId] = useState(null);

  useEffect(() => {
    api.get("/blog").then((r) => setPosts(r.data)).catch((e) => toast.error(fmtErr(e)));
  }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    try {
      if (editId) {
        const { data } = await api.put(`/admin/blog/${editId}`, form);
        setPosts((l) => l.map((p) => (p.id === editId ? data : p)));
        toast.success("Story updated");
      } else {
        const { data } = await api.post("/admin/blog", form);
        setPosts((l) => [data, ...(l || [])]);
        toast.success("Story published — it is live on the site");
      }
      setForm(EMPTY_POST);
      setEditId(null);
    } catch (err) {
      toast.error(fmtErr(err));
    }
  };

  const startEdit = (p) => {
    setForm({ title: p.title, body: p.body, image_url: p.image_url || "", author: p.author || "" });
    setEditId(p.id);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const remove = async (id) => {
    try {
      await api.delete(`/admin/blog/${id}`);
      setPosts((l) => l.filter((x) => x.id !== id));
      toast.success("Story deleted");
    } catch (err) {
      toast.error(fmtErr(err));
    }
  };

  if (!posts) return <p className="font-body text-navy/60">Loading stories…</p>;

  return (
    <div data-testid="admin-blog-tab">
      <form onSubmit={submit} className="grid sm:grid-cols-2 gap-3 mb-3" data-testid="blog-form">
        <input
          value={form.title}
          onChange={set("title")}
          placeholder="Story title *"
          required
          data-testid="blog-title-input"
          className={inputCls}
        />
        <input
          value={form.author}
          onChange={set("author")}
          placeholder="Written by (e.g. Team Wind Skaters)"
          data-testid="blog-author-input"
          className={inputCls}
        />
        <input
          value={form.image_url}
          onChange={set("image_url")}
          placeholder="Cover image URL (optional, https://...)"
          data-testid="blog-image-input"
          className={`${inputCls} sm:col-span-2`}
        />
        <textarea
          value={form.body}
          onChange={set("body")}
          placeholder="Your story * — leave a blank line between paragraphs"
          required
          rows={7}
          data-testid="blog-body-input"
          className={`${inputCls} resize-none sm:col-span-2`}
        />
        <div className="flex gap-3 sm:col-span-2">
          <button
            type="submit"
            data-testid="blog-submit-button"
            className="inline-flex items-center justify-center gap-2 bg-navy text-paper font-semibold px-8 py-2.5 rounded-full border-2 border-marigold hover:bg-verm hover:border-verm transition-colors duration-300"
          >
            {editId ? <Pencil className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
            {editId ? "Save Changes" : "Publish Story"}
          </button>
          {editId ? (
            <button
              type="button"
              onClick={() => {
                setEditId(null);
                setForm(EMPTY_POST);
              }}
              data-testid="blog-cancel-edit-button"
              className="font-semibold text-navy border-2 border-navy/20 px-6 py-2.5 rounded-full hover:border-verm hover:text-verm transition-colors duration-300"
            >
              Cancel
            </button>
          ) : null}
        </div>
      </form>
      {posts.length === 0 ? (
        <div className="border-2 border-dashed border-navy/25 rounded-2xl p-10 text-center font-body text-navy/60" data-testid="blog-admin-empty">
          No stories yet. Publish your first post above.
        </div>
      ) : (
        <div className="space-y-3" data-testid="blog-admin-list">
          {posts.map((p) => (
            <div
              key={p.id}
              className={`flex items-center justify-between gap-4 bg-paper border-2 rounded-xl p-4 ${
                editId === p.id ? "border-marigold" : "border-navy/15"
              }`}
              data-testid="blog-admin-item"
            >
              <div className="min-w-0">
                <p className="font-display font-bold text-navy truncate" data-testid="blog-admin-title">
                  {p.title}
                </p>
                <p className="font-body text-navy/60 text-xs mt-0.5" data-testid="blog-admin-meta">
                  {new Date(p.created_at).toLocaleDateString()} · {p.body.replace(/\n\n/g, " ").slice(0, 80)}…
                </p>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <button
                  onClick={() => startEdit(p)}
                  data-testid="blog-edit-button"
                  aria-label="Edit story"
                  className="text-navy/40 hover:text-navy transition-colors duration-300 p-2"
                >
                  <Pencil className="w-4 h-4" />
                </button>
                <button
                  onClick={() => remove(p.id)}
                  data-testid="blog-delete-button"
                  aria-label="Delete story"
                  className="text-navy/40 hover:text-verm transition-colors duration-300 p-2"
                >
                  <Trash2 className="w-5 h-5" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const Admin = () => {
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);
  const [tab, setTab] = useState("leads");
  const [content, setContent] = useState(null);

  useEffect(() => {
    api
      .get("/auth/me")
      .then((r) => setUser(r.data))
      .catch(() => setUser(null))
      .finally(() => setChecking(false));
  }, []);

  useEffect(() => {
    if (user) {
      api.get("/content").then((r) => setContent(r.data)).catch((e) => toast.error(fmtErr(e)));
    }
  }, [user]);

  if (checking) {
    return (
      <div className="min-h-screen bg-paper grid place-items-center" data-testid="admin-checking">
        <div className="animate-kite-bob" style={{ "--r": "-8deg" }}>
          <KiteMark className="w-14 h-14" />
        </div>
      </div>
    );
  }

  if (!user) return <Login onDone={setUser} />;

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } finally {
      setUser(null);
    }
  };

  return (
    <div className="min-h-screen bg-paper" data-testid="admin-dashboard">
      <FolkBorder />
      <header className="max-w-6xl mx-auto px-5 sm:px-8 py-6 flex flex-wrap items-center justify-between gap-4">
        <Logo />
        <div className="flex items-center gap-3">
          <span className="hidden sm:block font-body text-navy/60 text-sm" data-testid="admin-user-email">
            {user.email}
          </span>
          <button
            onClick={logout}
            data-testid="admin-logout-button"
            className="inline-flex items-center gap-2 font-semibold text-navy border-2 border-navy/20 px-5 py-2 rounded-full hover:border-verm hover:text-verm transition-colors duration-300"
          >
            <LogOut className="w-4 h-4" /> Logout
          </button>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-5 sm:px-8 pb-20">
        <h1 className="font-display font-bold text-navy text-4xl" data-testid="admin-heading">
          Mission Control
        </h1>
        <div className="mt-6 flex flex-wrap gap-2" data-testid="admin-tabs">
          {TABS.map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              data-testid={`admin-tab-${t.id}`}
              className={`inline-flex items-center gap-2 font-semibold px-5 py-2.5 rounded-full border-2 transition-colors duration-300 ${
                tab === t.id
                  ? "bg-navy text-paper border-marigold"
                  : "bg-transparent text-navy border-navy/20 hover:border-verm hover:text-verm"
              }`}
            >
              <t.icon className="w-4 h-4" />
              {t.label}
            </button>
          ))}
        </div>

        <div className="mt-8 bg-paperdeep/40 border-2 border-navy/15 rounded-2xl p-5 sm:p-8">
          {tab === "leads" && <LeadsTab />}
          {tab === "content" && content && <ContentTab content={content} onSaved={setContent} />}
          {tab === "gallery" && <GalleryTab />}
          {tab === "reels" && <ReelsTab />}
          {tab === "team" && <TeamTab />}
          {tab === "events" && <EventsTab />}
          {tab === "blog" && <BlogTab />}
        </div>
      </main>
    </div>
  );
};

export default Admin;
